import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, keyframes, styled, alpha } from '@mui/material';
import { fNumber } from '../../../../utils/formatNumber';

// Simple flash animation without scaling effects
const flash = keyframes`
  0% {
    opacity: 1;
    text-shadow: none;
  }
  50% {
    opacity: 0.9;
    text-shadow: 0 0 3px rgba(255, 255, 255, 0.4);
  }
  100% {
    opacity: 1;
    text-shadow: none;
  }
`;

const getTextColor = (color, theme) => {
  const isDarkTheme = theme.palette.mode === 'dark';
  
  // Return colors based on price movement direction
  if (color === 'up') {
    return isDarkTheme
      ? '#42d679' // Brighter green for dark theme
      : '#00701a'; // Darker green for light theme
  }
  if (color === 'down') {
    return isDarkTheme
      ? '#ff5c5c' // Brighter red for dark theme
      : '#b71c1c'; // Darker red for light theme
  }
  // Default color for no change
  return theme.palette.text.primary;
};

const StyledPrice = styled(Box)(({ theme, color, sx }) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  fontFamily: theme.typography.fontFamily,
  fontWeight: 700, // Increased from 600 for better visibility
  letterSpacing: -0.3,
  transition: theme.transitions.create(['color'], {
    duration: '0.1s', // Faster transition to match background changes
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  }),
  color: getTextColor(color, theme),
  textShadow: color 
    ? `0 0 1px ${alpha(theme.palette.background.paper, 0.4)}` // Increased shadow opacity
    : 'none', // Subtle text shadow for better readability
  '&.flash': {
    animation: `${flash} 0.6s ease-out`,
  },
  '& .decimal': {
    fontSize: '85%',
    opacity: 0.95, // Increased from 0.9 for better visibility
    marginLeft: 1,
  },
  ...(sx || {}),
}));

const formatPriceValue = (value) => {
  try {
    // Handle null, undefined, or invalid values
    if (value === null || value === undefined || value === '') {
      return { whole: '0', decimal: '00' };
    }

    // Convert to number and handle NaN
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(numValue)) {
      return { whole: '0', decimal: '00' };
    }

    // Format the number with 2 decimal places
    const formatted = Math.abs(numValue).toFixed(2);
    const [whole = '0', decimal = '00'] = formatted.split('.');

    return {
      whole: numValue < 0 ? `-${whole}` : whole,
      decimal
    };
  } catch (error) {
    console.error('Price formatting error:', error);
    return { whole: '0', decimal: '00' };
  }
};

const PriceWithColor = ({ price, variant = 'body2', customRender, sx }) => {
  const [color, setColor] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const prevPriceRef = useRef(price);

  useEffect(() => {
    const prevPrice = prevPriceRef.current;
    const currentPrice = Number(price);
    const previousPrice = Number(prevPrice);

    if (Number.isFinite(currentPrice) && Number.isFinite(previousPrice)) {
      if (currentPrice > previousPrice) {
        setColor('up');
        setIsFlashing(true);
      } else if (currentPrice < previousPrice) {
        setColor('down');
        setIsFlashing(true);
      }
    }

    const timer = setTimeout(() => {
      setIsFlashing(false);
      // Do NOT reset color after animation, maintain it to keep the background color visible
      // This ensures that color persists even after the animation
    }, 600);

    prevPriceRef.current = price;
    return () => clearTimeout(timer);
  }, [price]);

  if (customRender) {
    return (
      <StyledPrice color={color} className={isFlashing ? 'flash' : ''} sx={sx}>
        {customRender(price)}
      </StyledPrice>
    );
  }

  const { whole, decimal } = formatPriceValue(price);

  return (
    <Typography
      component="div"
      variant={variant}
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        ...(sx || {})
      }}
    >
      <StyledPrice color={color} className={isFlashing ? 'flash' : ''} sx={sx}>
        {whole}
        <span className="decimal">.{decimal}</span>
      </StyledPrice>
    </Typography>
  );
};

PriceWithColor.propTypes = {
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variant: PropTypes.string,
  customRender: PropTypes.func,
  sx: PropTypes.object,
};

export default PriceWithColor;
